import { FormEvent, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useLogin } from '../features/auth/useAuth';
import { useSessionStore, isTokenExpired } from '../stores/sessionStore';
import { authApi } from '../features/auth/authApi';
import { toast } from 'sonner';
import './login-page.css';

export function LoginPage() {
  const session = useSessionStore((state) => state.session);
  const clear = useSessionStore((state) => state.clear);
  const login = useLogin();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [logoUrl, setLogoUrl] = useState('https://inteligenciaenegocios.com.br/assets/logo.jpg');

  const token = session?.access_token;
  const expired = isTokenExpired(token);

  useEffect(() => {
    if (token && expired) {
      console.warn('[LoginPage] Token expirado encontrado na tela de login, limpando estado.');
      clear();
    }
  }, [token, expired, clear]);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const domain = window.location.hostname;
        const info = await authApi.tenantInfo(domain);
        if (info && info.logo_url) {
          setLogoUrl(info.logo_url);
        }
      } catch (err) {
        console.error('Failed to fetch tenant info', err);
      }
    };
    fetchLogo();
  }, []);

  if (token && !expired) {
    const from = (location.state as any)?.from?.pathname;
    const search = (location.state as any)?.from?.search || '';
    const target = from && from !== '/login' ? `${from}${search}` : '/';
    return <Navigate to={target} replace />;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    login.mutate({ email, password });
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Informe seu email no campo de login primeiro.');
      return;
    }

    setIsResetting(true);
    try {
      await authApi.forgotPassword({ identifier: email });
      toast.success('Se o e-mail existir, um link de redefinição foi enviado.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao solicitar redefinição de senha');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="login-screen">
      {/* Animated mesh gradient orbs */}
      <div className="mesh-gradient-bg">
        <div className="mesh-orb mesh-orb-1" />
        <div className="mesh-orb mesh-orb-2" />
        <div className="mesh-orb mesh-orb-3" />
      </div>

      <form className="login-card glass-panel" onSubmit={handleSubmit}>
        <div className="login-brand">
          <div className="logo-container">
            <img
              src={logoUrl}
              alt="Logo do CRM"
              className="brand-image"
            />
          </div>
        </div>

        <div className="login-header">
          <span className="eyebrow-premium">Acesso Restrito</span>
          <h1 className="login-title">Bem-vindo ao CRM</h1>
          <p className="login-subtitle">Acesse sua conta para gerenciar seus negócios.</p>
        </div>

        <div className="form-stack">
          <div className="form-field">
            <label htmlFor="email" className="field-label">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              className="premium-input"
              placeholder="seu@email.com"
            />
          </div>

          <div className="form-field">
            <label htmlFor="password" className="field-label">Senha</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              className="premium-input"
              placeholder="••••••••"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isResetting}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '0.85rem', 
                  color: 'var(--primary-color)', 
                  cursor: isResetting ? 'wait' : 'pointer',
                  padding: 0
                }}
              >
                {isResetting ? 'Enviando...' : 'Esqueceu a senha?'}
              </button>
            </div>
          </div>
        </div>

        {login.error ? (
          <div className="error-box animate-shake">{login.error.message}</div>
        ) : null}

        <button className="premium-button" type="submit" disabled={login.isPending}>
          {login.isPending ? (
            <span className="btn-loading">
              <span className="spinner-sm" />
              Entrando...
            </span>
          ) : (
            'Entrar'
          )}
        </button>

        <p className="login-footer text-center">
          <a className="login-landing-link" href="https://crm-platform-suite.inteligenciaenegocios.com.br/" target="_blank" rel="noopener noreferrer">
            Conheça o CRM Platform Suite
          </a>
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '12px', fontSize: '0.78rem', opacity: 0.85 }}>
          <a href="/privacidade" style={{ color: '#9ca3af', textDecoration: 'underline' }}>Política de Privacidade</a>
          <span style={{ color: '#4b5563' }}>•</span>
          <a href="/termos" style={{ color: '#9ca3af', textDecoration: 'underline' }}>Termos de Serviço</a>
          <span style={{ color: '#4b5563' }}>•</span>
          <a href="/exclusao-de-dados" style={{ color: '#9ca3af', textDecoration: 'underline' }}>Exclusão de Dados</a>
        </div>
      </form>
    </div>
  );
}
